<template>
    <table class="table table-fixed" :class="{
        'table-hover': hover,
        'table-striped': striped,
        'table-condensed': condensed,
        'table-admin': admin,
    }">
        <!-- Columns -->
        <colgroup v-if="!empty(columnsParsed)">
            <col v-if="selectable" width="35" class="hidden-print">
            <col v-for="column in columnsParsed" :key="column.key"
                 :width="column.width" :class="{'hidden-print': !column.print}">
        </colgroup>

        <thead>
            <!-- Main Header -->
            <tr :class="{ headers: showFilters }">
                <th v-if="selectable" class="hidden-print">
                    <input
                      type="checkbox"
                      v-model="selectAll"
                      :indeterminate.prop="selection.length > 0 && selection.length !== data.length"
                    >
                </th>

                <!-- Sortable Columns -->
                <th v-for="column in columnsParsed"
                    :key="column.key"
                    :class="{
                        'pointer-sort': column.sort,
                        'sorted': column.sort && sort.key === column.key,
                        'hidden-print': !column.print,
                    }"
                    @click="toggleSort(column)"
                    :title="column.title">
                    <div class="title">
                        {{ column.name }}

                        <span v-if="column.sort"
                              class="text-muted sort-button"
                              :class="{active: sort.key === column.key, 'hidden-print': sort.key !== column.key}">
                            <i class="glyphicon" :class="sortClass(column)"></i>
                        </span>
                    </div>
                </th>
            </tr>

            <!-- Filters -->
            <tr v-if="showFilters" class="filters hidden-print">
                <th v-if="selectable" class="hidden-print"></th>
                <th v-for="column in columnsParsed" :key="column.key" class="form-group-sm"
                    :class="{'no-filter': !column.filter, 'sorted': column.sort && sort.key === column.key}">
                    <template v-if="column.filter">
                        <div class="input-group">
                            <!-- Enums -->
                            <select v-if="column.values" class="form-control input-sm" v-model="queries[column.key]">
                                <option selected></option>
                                <option v-for="(value, index) in values(column)" :key="index"
                                        :value="index" v-text="value" />
                            </select>

                            <!-- Other values -->
                            <input class="form-control input-sm" v-model.trim="queries[column.key]" v-else>

                            <div v-if="queries[column.key] !== ''" class="input-group-btn">
                                <button class="btn btn-default" type="button"
                                        tabindex="-1"
                                        @click="queries[column.key] = ''">&times;</button>
                            </div>
                        </div>
                    </template>
                </th>
            </tr>
        </thead>

        <tbody>
            <!-- Empty state -->
            <tr v-if="empty(sorted)" class="muted disabled">
                <td class="text-center text-muted" :colspan="columnLength" v-text="dataEmpty" />
            </tr>

            <!-- Data Rows -->
            <tr v-for="entry in sorted" :key="fetchKey(entry)"
                @click="handleClick(entry)" :class="{
                'active': isActive(entry),
                'success': fetchValue(entry, stateSuccess),
                'warning': fetchValue(entry, stateWarning),
                'danger': fetchValue(entry, stateDanger),
                'pointer': click !== null && click !== undefined,
            }">
                <td v-if="selectable" class="hidden-print"
                    @click.stop="isSelected(entry) ? removeSelection(entry) : addSelection(entry)">
                    <input type="checkbox" :checked="isSelected(entry)">
                </td>

                <slot v-for="column in columnsParsed" :name="column.key" :entry="entry"
                      :value="$scopedSlots[column.key] !== undefined ? content(entry, column, false) : null">
                    <td :key="column.key" :class="{
                        'text-numeric': column.number,
                        'text-strong':  column.strong,
                        'text-right':   column.align === 'right',
                        'text-center':  column.align === 'center',
                        'hidden-print': !column.print,
                    }" v-html="content(entry, column, false)" />
                </slot>
            </tr>
        </tbody>

        <tfoot>
            <!-- Footer -->
            <slot name="_footer">
                <tr>
                    <td :colspan="columnLength">
                        <div class="media" style="display: block">
                            <div class="media-body">
                                <span v-if="data.length !== sorted.length">{{ sorted.length }} aus </span>
                                {{ data.length }} {{ data.length === 1 ? 'Eintrag' : (data.length !== sorted.length ? 'Einträgen' : 'Einträge') }}
                            </div>
                            <div class="media-right hidden-print">
                                <button type="button" class="btn btn-xs btn-default" @click="printTable">
                                    <i class="mdi mdi-print"></i> Tabelle Drucken
                                </button>
                            </div>
                        </div>
                    </td>
                </tr>
            </slot>
        </tfoot>
    </table>
</template>

<script>
import _ from 'lodash';
import { assert } from '../utils/assert';
import print from '../utils/print';

const val = (value, def) => (value === undefined ? def : value);

export default {
  props: {
    sort: {
      type: Object,
    },

    sortBy: {
      type: [String, Function],
    },

    data: {
      type: Array,
      required: true,
    },

    keyBy: {
      type: [String, Function],
      required: false,
      default: '$index',
    },

    columns: {
      type: Array,
    },

    condensed: {
      type: Boolean,
      default: false,
    },

    hover: {
      type: Boolean,
      default: true,
    },

    striped: {
      type: Boolean,
      default: true,
    },

    admin: {
      type: Boolean,
      default: false,
    },

    click: {
      type: Function,
    },

    active: {
      type: [Object, Number, Function],
    },

    selectable: {
      type: Boolean,
      default: false,
    },

    stateSuccess: {
      type: [String, Function],
    },

    stateWarning: {
      type: [String, Function],
    },

    stateDanger: {
      type: [String, Function],
    },

    dataEmpty: {
      type: String,
      default: 'Keine Daten vorhanden',
    },

    selected: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    const noFilterCount = _.sumBy(this.columns, ({ filter }) => (filter === false ? 1 : 0));

    return {
      showFilters: noFilterCount < this.columns.length,
      selectAll: false,
      queries: _.reduce(this.columns, (result, entry) => {
        if (entry.query !== undefined) {
          result[entry.key] = `${entry.query}`;
        } else {
          result[entry.key] = '';
        }

        return result;
      }, {}),
    };
  },

  computed: {
    columnLength() {
      return this.columnsParsed.length + (this.selectable ? 1 : 0);
    },

    columnsParsed() {
      return _.map(this.columns, column => ({
        key: column.key,
        name: column.name,
        width: val(column.width, '*'),
        align: val(column.align, 'left'),
        strong: val(column.strong, false),
        number: val(column.number, false),
        highlight: val(column.number, false),
        numeric: val(column.numeric, false),
        boolean: val(column.boolean, this.queries[column.key] === 'true' || this.queries[column.key] === 'false'),
        sort: val(column.sort, true),
        filter: val(column.filter, true),
        values: _.isArray(column.values) ? _.zipObject(column.values, column.values) : column.values,
        value: column.value,
        filterValue: val(column.filterValue, column.value),
        sortValue: val(column.sortValue, val(column.filterValue, column.value)),
        print: val(column.print, true),
      }));
    },

    columnsByKey() {
      return _.reduce(this.columnsParsed, (result, column) => {
        result[column.key] = column;
        return result;
      }, {});
    },

    filtered() {
      if (!this.showFilters) {
        return this.data;
      }

      const { content, columnsByKey: columns } = this;
      const getFilter = this.getFilterFunction;

      // create a map of values -> functions that get executed
      const filters = _.transform(this.queries, (result, str, key) => {
        result[key] = (str === '' || !columns[key].filter) ? null : getFilter(key, str);
      }, {});

      // filter against the (hopefully plain-text) content value
      return _.filter(this.data, row => (_.every(this.columnsParsed, (column) => {
        const func = filters[column.key];
        return !func || func(content(row, column, true));
      })));
    },

    sorted() {
      const func = this.sortBy;
      const { key } = this.sort;
      const columns = this.columnsByKey;

      if (func !== undefined) {
        return _.orderBy(this.filtered, entry => (func(entry, key)));
      }

      return _.orderBy(this.filtered, (entry) => {
        const column = columns[key];
        assert(column, `No column with key ${key} found`);

        // Check if we got a special value method in the column settings
        const valueFunc = column.sortValue;
        let value;

        if (valueFunc !== undefined) {
          value = _.isString(valueFunc) ? _.get(entry, valueFunc) : valueFunc(entry);
        } else {
          // Try to check if we have a value in the entry itself
          value = entry[key];
        }

        return _.isString(value) ? value.toLowerCase() : value;
      }, this.sort.order);
    },

    selection: {
      get() {
        return this.selected;
      },
      set(array) {
        this.$emit('update:selected', array);
      },
    },
  },

  watch: {
    selectAll(value) {
      this.selection = value ? this.filtered.slice() : [];
    },
  },

  methods: {
    printTable() {
      print(this.$el);
    },

    sortClass(column) {
      const order = this.sort.key === column.key ? this.sort.order : (column.number ? 'desc' : 'asc');
      let name = `glyphicon-sort-by-${column.number ? 'attributes' : 'alphabet'}`;

      if (order === 'desc') {
        name = `${name}-alt`;
      }

      return [name];
    },

    toggleSort(column) {
      if (column.sort) {
        if (this.sort.key === column.key) {
          this.$set(this.sort, 'order', this.sort.order === 'asc' ? 'desc' : 'asc');
        } else {
          this.$set(this.sort, 'key', column.key);
          this.$set(this.sort, 'order', column.number ? 'desc' : 'asc');
        }
      }
    },

    handleClick(entry) {
      if (this.click) {
        this.click(entry);
      }
    },

    isActive(entry) {
      const { active } = this;

      if (typeof active === 'number') {
        entry = this.fetchKey(entry);
      } else if (_.isFunction(active)) {
        return active(entry);
      }

      return entry === active;
    },

    fetchKey(entry) {
      if (_.isString(this.keyBy)) {
        return _.get(entry, this.keyBy);
      }

      return this.keyBy(entry);
    },

    fetchValue(entry, val) {
      if (_.isString(val)) {
        // treat value as key and get value from entry
        return _.get(entry, val);
      } else if (_.isFunction(val)) {
        // we got a custom function
        return val(entry);
      }

      if (val !== undefined) {
        console.error(`Invalid value function: ${val}`);
      }
    },

    content(entry, column, filtering) {
      // The "value" can either be a string or a function
      const val = filtering ? column.filterValue : column.value;
      let func;

      if (_.isString(val)) {
        // treat value as key and get value from entry
        func = ~val.indexOf('.') ? entry => (_.get(entry, val)) : entry => (entry[val]);
      } else if (_.isFunction(val)) {
        // we got a custom function
        func = val;
      } else if (val !== undefined) {
        // we do not support anything else
        console.error(`Invalid value function: ${val}`);
      } else if (val === undefined) {
        func = entry => (entry[column.key]);
      } else {
        // treat entry as a string by itself
        func = entry => (entry);
      }

      const value = func(entry);

      // Highlight content when we're filtering
      if (this.showFilters && !column.numeric && column.values === undefined && typeof value === 'string') {
        const query = this.queries[column.key];

        if (query !== '') {
          const regex = new RegExp(_.escapeRegExp(query), 'i');
          return value.replace(regex, token => `<span class="table-highlight">${token}</span>`);
        }
      }

      return value;
    },

    getFilterFunction(key, query) {
      const column = this.columnsByKey[key];

      // Treat numbers differently
      if (column.numeric) {
        // match <, >, = and the following number
        const match = /([!><=]{1,2})?(?:\s+)?([-\d]+)/g.exec(query);

        return (value) => {
          if (match) {
            const number = parseInt(match[2], 10);
            const that = parseInt(value, 10);

            switch (match[1]) {
              case '>': return that > number;
              case '<': return that < number;
              case '=>': case '>=': return that >= number;
              case '=<': case '<=': return that <= number;
              case '<>': case '><': case '!': return that != number;
              default: return that == number;
            }
          }

          return true;
        };
      } else if (column.boolean) {
        // Convert query to boolean for the normal check to work
        query = (query === 'true');
      } else if (typeof query === 'string') {
        // Standard regex
        const regex = new RegExp(_.escapeRegExp(query), 'i');
        return value => (regex.test(value));
      } else {
        query = `${query}`;
      }

      // eslint-disable-next-line eqeqeq
      return value => (value == query);
    },

    values(column) {
      if (!column.values) {
        return undefined;
      }

      return _.isFunction(column.values) ? column.values() : column.values;
    },

    isSelected(entry) {
      return this.selection.indexOf(entry) >= 0;
    },

    addSelection(entry) {
      this.selection = [...this.selection, entry];
    },

    removeSelection(entry) {
      this.selection.splice(this.selection.indexOf(entry), 1);
    },
  },
};
</script>

<style lang="scss" scoped>
    @import "../../sass/variables";

    th {
        position: relative;

        .sort-button {
            display: none;
        }

        &:hover .sort-button, .sort-button.active {
            display: inline;
        }

        &:hover .sort-button:not(.active) {
            opacity: .5;
        }

        &.sorted {
            font-weight: 700;
            background-color: #f6f6f6;
        }
    }

    th.filter-button {
        color: #333 !important;
        text-align: center;
        overflow: visible !important;

        &:hover {
            background: #eee;
        }

        &.active {
            background-color: $kw-blue-lighter;
            color: white !important;
        }

        strong {
            font-size: 20px;
            line-height: 10px;
            vertical-align: middle;
        }
    }

    .flex-item > .table.table-admin thead th .filter-button {
        width: 40px;
        padding-top: 8px;
    }

    td, th {
        overflow: hidden;
        white-space: nowrap;
        word-wrap: break-word;
        text-overflow: ellipsis;
    }

    td > div:not(.exam-grade-cell) {
        display: inline;
    }

    tr.headers th {
        border-bottom: 0;
        padding-bottom: 5px;
    }

    tr.filters th {
        padding: 0 3px 2px !important;
        border-top-width: 0;

        input, select {
            height: 25px;
        }

        input {
            padding: 2px 4px;
            font-weight: 500;
        }

        select {
            line-height: 15px;
        }

        .input-group {
            width: 100%;
        }

        .input-group-btn > .btn {
            height: 25px;
            line-height: 1;
            border-radius: 0;
            padding-left: 4px;
            padding-right: 4px;
        }
    }

    tfoot {
        border-top: 2px solid #eee;
        font-size: 13px;
        font-weight: 500;
    }
</style>

<style>
    .table-highlight {
        background: rgba(255, 200, 0, 0.666);
        padding: 5px 0 4px;
    }
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/table.vue